/**
 * Common ShowcaseUnit component used by other modules as a reusable component which returns the ui for a ShowcaseUnit card.
 *
 * @module views/Molecules/ShowcaseUnit
 * @memberof -Common
 */
import './ShowcaseUnit.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Spacer from '@ulta/core/components/Spacer/Spacer';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import ProductRailHorizontalProvider from '@ulta/providers/ProductRailHorizontalProvider/ProductRailHorizontalProvider';

import ShowcaseProductRail from '@ulta/components/ShowcaseProductRail/ShowcaseProductRail';

/**
 * Represents a ShowcaseUnit component
 *
 * @method
 * @param {ShowcaseUnitProps} props - React properties passed from composition
 * @returns ShowcaseUnit
 */
export const ShowcaseUnit = function( props ){
  const { items, showcaseMedia, spacerValue } = props;

  if( !hasItems( items ) || !showcaseMedia ){
    return null;
  }

  const initializedProps = {
    ...defaultProps,
    ...props
  };

  return (
    <div className='ShowcaseUnit'>
      <ProductRailHorizontalProvider>
        <ShowcaseProductRail
          { ...spreadProps( { ...initializedProps } ) }
        />
        {
          spacerValue &&
          <Spacer value={ spacerValue } />
        }
      </ProductRailHorizontalProvider>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} deviceType='' - The default value of the deviceType is empty string.
 * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
 * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
 * @property {boolean} ssr=false - The default value for ssr is set to false.
 * @property {string} title='' - The default value of the title is empty string.
 * @property {string} countLabel='' - The default value of the count label is empty string.
 * @property {boolean} arrows=false - The default value for arrows is set to false.
 * @property {number} slidesToSlide=2 - The default value for slidesToSlide is set to 2.
 */
export const defaultProps = {
  deviceType: '',
  previousAccessibility: '',
  nextAccessibility: '',
  ssr: false,
  title: '',
  countLabel: '',
  arrows: false,
  slidesToSlide: 2
};

/**
 * Property type definitions
 * @typedef ShowcaseUnitProps
 * @type {object}
 * @property {string} deviceType - Sets the device type.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {boolean} ssr - Sets server side rendering.
 * @property {string} title - Sets the title for productRail list.
 * @property {string} countLabel - Sets the count label for productRail list.
 * @property {boolean} arrows - Sets whether to show or hide default arrows on carousel.
 * @property {number} slidesToSlide - Sets the number of slides to slide when clicked on next or previous button.
 * @property {object} viewAllAction - This is the action object with action label and url.
 * @property {string} carouselAccessibility - Sets the carousel role='Carousel' attribute value
 * @property {string} carouselDetailsAccessibility - Sets the Carousel details screen reader information
 * @property {string} carouselCardAccessibility - Sets the carousel item number of total count string
 */
export const propTypes = {
  deviceType: PropTypes.string,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string,
  ssr: PropTypes.bool,
  title: PropTypes.string,
  countLabel: PropTypes.string,
  arrows: PropTypes.bool,
  slidesToSlide: PropTypes.number,
  viewAllAction: PropTypes.object,
  carouselAccessibility: PropTypes.string,
  carouselDetailsAccessibility: PropTypes.string,
  carouselCardAccessibility: PropTypes.string
};

ShowcaseUnit.propTypes = propTypes;
ShowcaseUnit.defaultProps = defaultProps;

export default ShowcaseUnit;